import {
  Card,
  Grid,
  CardContent,
  Typography,
  Box,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  FormLabel,
  useMediaQuery
} from '@mui/material';
import { Control, Controller, FieldErrors, useWatch } from 'react-hook-form';
import { Focused } from 'react-credit-cards-2';
import { internalCard } from '../../styles';
import { Input } from '~/components';
import CardCredit from '~/components/CardCredit';
import { useState } from 'react';
import { Cards } from '../../interfaces/contract';
import { VisaIcon } from '~/assets/icons/CreditCard/visa';
import { AmexIcon } from '~/assets/icons/CreditCard/amex';
import { DinersIcons } from '~/assets/icons/CreditCard/diners';
import { EloIcon } from '~/assets/icons/CreditCard/elo';
import { HiperCardIcon } from '~/assets/icons/CreditCard/hipercard';
import { MastercardIcon } from '~/assets/icons/CreditCard/mastercard';

export interface CardData {
  card_number: string;
  card_holder_name: string;
  card_expiration_date: string;
  card_cvv: string;
}

export interface Props {
  control: Control<CardData, object>;
  errors: FieldErrors<CardData>;
  savedCards: Cards[];
  selectedCardId: string;
  setSelectedCardId: React.Dispatch<React.SetStateAction<string>>;
}
const BRAND_MAP: Record<string, JSX.Element> = {
  amex: <AmexIcon />,
  diners: <DinersIcons />,
  elo: <EloIcon />,
  hipercard: <HiperCardIcon />,
  mastercard: <MastercardIcon />,
  visa: <VisaIcon />
};
export function PaymentCard({ control, errors, savedCards, selectedCardId, setSelectedCardId }: Props) {
  const [focusedAt, setFocusedAt] = useState<Focused>('number');

  const isNewCard = selectedCardId === 'new';
  const showCardPreview = useMediaQuery('(min-width: 1200px)');

  const number = useWatch({ control, name: 'card_number', defaultValue: '' });
  const name = useWatch({ control, name: 'card_holder_name', defaultValue: '' });
  const expiry = useWatch({ control, name: 'card_expiration_date', defaultValue: '' });
  const cvv = useWatch({ control, name: 'card_cvv', defaultValue: '' });

  return (
    <Card sx={{ margin: { xs: '2rem 0.5rem', sm: '2rem 1rem', md: '2rem 2rem' } }} id="payment-card">
      <CardContent sx={internalCard}>
        <Typography variant="h6" align="left" sx={{ paddingBottom: 1 }}>
          4 - Dados para o pagamento
        </Typography>

        <FormControl component="fieldset" sx={{ marginBottom: 3 }}>
          <FormLabel component="legend">Escolha a forma de pagamento</FormLabel>
          <RadioGroup value={selectedCardId} onChange={(e) => setSelectedCardId(e.target.value)}>
            {savedCards.map((card) => {
              const key = card.brand.toLowerCase();
              return (
                <FormControlLabel
                  key={card.id}
                  value={card.id}
                  control={<Radio />}
                  label={
                    <Box display="flex" alignItems="center" gap={1}>
                      {BRAND_MAP[key]}

                      <Typography component="span">**** **** {card.lastFourDigits}</Typography>
                    </Box>
                  }
                />
              );
            })}

            <FormControlLabel value="new" control={<Radio />} label="Novo cartão de crédito" />
          </RadioGroup>
        </FormControl>
        {isNewCard && (
          <Grid container spacing={4}>
            <Grid item xs={12} lg={6}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Controller
                    name="card_number"
                    control={control}
                    render={({ field }) => (
                      <Input
                        label="Número do cartão"
                        placeholder="XXXX XXXX XXXX XXXX"
                        mask="cardNumber"
                        error={isNewCard && !!errors?.card_number}
                        helperText={isNewCard ? errors?.card_number?.message : ''}
                        {...field}
                        onFocus={() => setFocusedAt('number')}
                        disabled={!isNewCard}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Controller
                    name="card_holder_name"
                    control={control}
                    render={({ field }) => (
                      <Input
                        label="Nome no cartão"
                        placeholder="João da Silva"
                        error={isNewCard && !!errors?.card_holder_name}
                        helperText={isNewCard ? errors?.card_holder_name?.message : ''}
                        {...field}
                        onFocus={() => setFocusedAt('name')}
                        disabled={!isNewCard}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={6}>
                  <Controller
                    name="card_expiration_date"
                    control={control}
                    render={({ field }) => (
                      <Input
                        label="Expiração"
                        placeholder="01/31"
                        error={isNewCard && !!errors?.card_expiration_date}
                        helperText={isNewCard ? errors?.card_expiration_date?.message : ''}
                        {...field}
                        mask="expiredDate"
                        onFocus={() => setFocusedAt('expiry')}
                        disabled={!isNewCard}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={6}>
                  <Controller
                    name="card_cvv"
                    control={control}
                    render={({ field }) => (
                      <Input
                        label="CVV"
                        placeholder="123"
                        error={isNewCard && !!errors?.card_cvv}
                        helperText={isNewCard ? errors?.card_cvv?.message : ''}
                        {...field}
                        mask="cvv"
                        onFocus={() => setFocusedAt('cvc')}
                        disabled={!isNewCard}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </Grid>

            {showCardPreview && (
              <Grid item xs={12} lg={6} sx={{ position: 'relative' }}>
                <Box
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)'
                  }}
                >
                  <CardCredit number={number} cvc={cvv} expiry={expiry} focused={focusedAt} name={name} />
                </Box>
              </Grid>
            )}
          </Grid>
        )}
      </CardContent>
    </Card>
  );
}
